import { supabase } from "../integrations/supabase/client";
import { getMultiplePublicProfiles } from "./secureProfileService";
import { sendLikeNotification, sendCommentNotification } from "./triggerNotification";
import { optimizeImage } from "../utils/imageOptimization";

// 🔒 SECURE FILTER UTILITY - Filters sensitive user data from posts
const filterSafeUserFields = (userData: any) => {
  if (!userData) return null;
  
  return {
    id: userData.id,
    full_name: userData.full_name,
    avatar_url: userData.avatar_url,
    bio: userData.bio,
    location: userData.location,
    created_at: userData.created_at
    // ❌ Excluded: email, subscription_tier, payment_provider, etc.
  };
};

// 🔒 SECURE FILTER FOR POST DATA - Applies filtering to entire post object
const filterPostData = (post: any) => {
  if (!post) return null;
  
  return {
    ...post,
    // Filter user data if present (for posts that include user info)
    user: post.user ? filterSafeUserFields(post.user) : undefined,
    // Keep pet data as-is (already safe)
    pet: post.pet,
    // Keep all other post fields unchanged
    likes_count: post.likes_count,
    comments_count: post.comments_count,
    user_has_liked: post.user_has_liked,
  };
};

export async function getFeedPosts(page = 0, limit = 10) {
  const from = page * limit;
  const to = from + limit - 1;
  
  const { data, error } = await supabase
    .from('memory_posts')
    .select(`
      *,
      pet:pets(*),
      media:media_files(id, url, media_type),
      likes:likes(id),
      comments:comments(id)
    `)
    .eq('privacy_setting', 'public')
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error) {
    throw new Error(`Error fetching posts: ${error.message}`);
  }

  // 🔒 SECURE TRANSFORMATION - Apply filtering while preserving functionality
  const postsWithCounts = data.map(post => ({
    ...post,
    likes_count: post.likes ? post.likes.length : 0,
    comments_count: post.comments ? post.comments.length : 0,
    // Keep only the data we need from the pet
    pet: post.pet ? {
      id: post.pet.id,
      name: post.pet.name,
      photo_url: post.pet.photo_url,
      is_memorial: post.pet.is_memorial,
      species: post.pet.species,
      breed: post.pet.breed,
      user_id: post.pet.user_id
    } : null,
    // Check if current user has liked the post
    // This would require knowing the user ID, which will be handled client-side
    user_has_liked: false, // Default value, will be updated client-side
    post_type: post.post_type || 'memory', // ✅ NEW: Include post_type
    contact_phone: post.contact_phone ?? null,
    lost_lat: post.lost_lat ?? null,
    lost_lng: post.lost_lng ?? null,
    lost_address: post.lost_address ?? null,
    lost_last_seen_at: post.lost_last_seen_at ?? null,
  }));

  return postsWithCounts;
}

export async function getUserPosts(userId) {
  if (!userId) {
    return [];
  }
  
  const { data, error } = await supabase
    .from('memory_posts')
    .select(`
      *,
      pet:pets(*),
      media:media_files(id, url, media_type),
      likes:likes(id),
      comments:comments(id)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Error fetching user posts: ${error.message}`);
  }

  if (!data) {
    return [];
  }

  // 🔒 SECURE TRANSFORMATION - Apply filtering while preserving functionality
  const postsWithCounts = data.map((post) => {
    return {
      ...post,
      likes_count: post.likes ? post.likes.length : 0,
      comments_count: post.comments ? post.comments.length : 0,
      pet: post.pet ? {
        id: post.pet.id,
        name: post.pet.name,
        photo_url: post.pet.photo_url,
        is_memorial: post.pet.is_memorial,
        species: post.pet.species,
        breed: post.pet.breed,
        user_id: post.pet.user_id
      } : null,
      user_has_liked: false, // Default value, will be updated client-side
      post_type: post.post_type || 'memory', // ✅ NEW: Include post_type
      contact_phone: post.contact_phone ?? null,
      lost_lat: post.lost_lat ?? null,
      lost_lng: post.lost_lng ?? null,
      lost_address: post.lost_address ?? null,
      lost_last_seen_at: post.lost_last_seen_at ?? null,
    };
  });
  
  return postsWithCounts;
}

// Function to get all posts from all of a user's pets
export async function getAllUserPetPosts(userId) {
  if (!userId) {
    return [];
  }
  
  try {
    // First, get all pets belonging to this user
    const { data: userPets, error: petsError } = await supabase
      .from('pets')
      .select('id')
      .eq('user_id', userId);
    
    if (petsError) {
      throw new Error(`Error fetching user pets: ${petsError.message}`);
    }
    
    if (!userPets || userPets.length === 0) {
      return [];
    }
    
    const petIds = userPets.map(pet => pet.id);
    
    // Now get all posts for these pets
    const { data, error } = await supabase
      .from('memory_posts')
      .select(`
        *,
        pet:pets(*),
        media:media_files(id, url, media_type),
        likes:likes(id),
        comments:comments(id)
      `)
      .in('pet_id', petIds)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Error fetching user pet posts: ${error.message}`);
    }

    if (!data) {
      return [];
    }

    // 🔒 SECURE TRANSFORMATION - Apply filtering while preserving functionality
    const postsWithCounts = data.map((post) => {
      return {
        ...post,
        likes_count: post.likes ? post.likes.length : 0,
        comments_count: post.comments ? post.comments.length : 0,
        pet: post.pet ? {
          id: post.pet.id,
          name: post.pet.name,
          photo_url: post.pet.photo_url,
          is_memorial: post.pet.is_memorial,
          species: post.pet.species,
          breed: post.pet.breed,
          user_id: post.pet.user_id
        } : null,
        user_has_liked: false, // Default value, will be updated client-side
        post_type: post.post_type || 'memory', // ✅ NEW: Include post_type
        contact_phone: post.contact_phone ?? null,
        lost_lat: post.lost_lat ?? null,
        lost_lng: post.lost_lng ?? null,
        lost_address: post.lost_address ?? null,
        lost_last_seen_at: post.lost_last_seen_at ?? null,
      };
    });
    
    return postsWithCounts;
  } catch (error) {
    console.error('Error in getAllUserPetPosts:', error);
    throw error;
  }
}

export async function getPetPosts(petId) {
  if (!petId) {
    return [];
  }
  
  const { data, error } = await supabase
    .from('memory_posts')
    .select(`
      *,
      pet:pets(*),
      media:media_files(id, url, media_type),
      likes:likes(id),
      comments:comments(id)
    `)
    .eq('pet_id', petId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Error fetching pet posts: ${error.message}`);
  }

  // 🔒 SECURE TRANSFORMATION - Apply filtering while preserving functionality
  const postsWithCounts = data.map(post => ({
    ...post,
    likes_count: post.likes ? post.likes.length : 0,
    comments_count: post.comments ? post.comments.length : 0,
    pet: post.pet ? {
      id: post.pet.id,
      name: post.pet.name,
      photo_url: post.pet.photo_url,
      is_memorial: post.pet.is_memorial,
      species: post.pet.species,
      breed: post.pet.breed,
      user_id: post.pet.user_id
    } : null,
    user_has_liked: false, // Default value, will be updated client-side
    post_type: post.post_type || 'memory', // ✅ NEW: Include post_type
    contact_phone: post.contact_phone ?? null,
    lost_lat: post.lost_lat ?? null,
    lost_lng: post.lost_lng ?? null,
    lost_address: post.lost_address ?? null,
    lost_last_seen_at: post.lost_last_seen_at ?? null,
  }));

  return postsWithCounts;
}

export async function getPostById(postId) {
  // Get current user (may be null for unauthenticated users)
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('memory_posts')
    .select(`
      *,
      pet:pets(*),
      media:media_files(id, url, media_type),
      likes:likes(id),
      comments:comments(id)
    `)
    .eq('id', postId)
    .single();

  if (error) {
    throw new Error(`Error fetching post: ${error.message}`);
  }

  // 🔒 PRIVACY CHECK: Validate if user can access this post
  if (data.privacy_setting !== 'public') {
    // If post is private and user is not authenticated or not the owner
    if (!user || data.user_id !== user.id) {
      throw new Error('Este post no está disponible públicamente');
    }
  }

  // 🔒 SECURE TRANSFORMATION - Apply filtering while preserving functionality
  const postWithCounts = {
    ...data,
    likes_count: data.likes ? data.likes.length : 0,
    comments_count: data.comments ? data.comments.length : 0,
    pet: data.pet ? {
      id: data.pet.id,
      name: data.pet.name,
      photo_url: data.pet.photo_url,
      is_memorial: data.pet.is_memorial,
      species: data.pet.species,
      breed: data.pet.breed,
      user_id: data.pet.user_id
    } : null,
    user_has_liked: false, // Default value, will be updated client-side
    post_type: data.post_type || 'memory', // ✅ NEW: Include post_type
    contact_phone: data.contact_phone ?? null,
    lost_lat: data.lost_lat ?? null,
    lost_lng: data.lost_lng ?? null,
    lost_address: data.lost_address ?? null,
    lost_last_seen_at: data.lost_last_seen_at ?? null,
  };

  return postWithCounts;
}

export async function likePost(postId) {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user || !user.id) {
      throw new Error("User not authenticated");
    }

    // Check if the user has already liked this post
    const { data: existingLike, error: checkError } = await supabase
      .from('likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (checkError) {
      throw new Error(`Error checking existing like: ${checkError.message}`);
    }

    if (existingLike) {
      return existingLike; // User already liked this post
    }

    // Get post owner to send notification
    const { data: post, error: postError } = await supabase
      .from('memory_posts')
      .select('user_id')
      .eq('id', postId)
      .single();

    const { data, error } = await supabase
      .from('likes')
      .insert({ post_id: postId, user_id: user.id })
      .select()
      .single();

    if (error) {
      throw new Error(`Error liking post: ${error.message}`);
    }

    // Send notification to post owner (if not the same user)
    if (post && post.user_id !== user.id) {
      try {
        await sendLikeNotification(post.user_id, user.id, postId);
      } catch (notificationError) {
        console.error('Error sending like notification:', notificationError);
        // Don't throw - like was successful, notification failure shouldn't break the flow
      }
    }

    return data;
  } catch (error) {
    console.error('Error in likePost:', error);
    throw error;
  }
}

export async function unlikePost(postId) {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user || !user.id) {
      throw new Error("User not authenticated");
    }

    const { error } = await supabase
      .from('likes')
      .delete()
      .eq('post_id', postId)
      .eq('user_id', user.id);

    if (error) {
      throw new Error(`Error unliking post: ${error.message}`);
    }

    return true;
  } catch (error) {
    console.error('Error in unlikePost:', error);
    throw error;
  }
}

export async function addComment(postId, content, parentCommentId) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user || !user.id) {
    throw new Error("User not authenticated");
  }

  // Get post owner to send notification
  const { data: post, error: postError } = await supabase
    .from('memory_posts')
    .select('user_id')
    .eq('id', postId)
    .single();

  const commentData = {
    post_id: postId,
    user_id: user.id,
    content,
    ...(parentCommentId && { parent_comment_id: parentCommentId })
  };

  const { data, error } = await supabase
    .from('comments')
    .insert(commentData)
    .select()
    .single();

  if (error) throw new Error(`Error adding comment: ${error.message}`);

  // Send notification to post owner (if not the same user)
  if (post && post.user_id !== user.id) {
    try {
      await sendCommentNotification(post.user_id, user.id, postId);
    } catch (notificationError) {
      console.error('Error sending comment notification:', notificationError);
      // Don't throw - comment was successful, notification failure shouldn't break the flow
    }
  }

  return data;
}

export async function getPostComments(postId) {
  // Fetch comments WITHOUT joining users (RLS-safe)
  const { data, error } = await supabase
    .from('comments')
    .select('*')
    .eq('post_id', postId)
    .is('parent_comment_id', null)
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Error fetching comments: ${error.message}`);

  const comments = data || [];

  // Calculate replies count for each comment
  const commentIds = comments.map((c: any) => c.id);
  
  let repliesCountMap = new Map<string, number>();
  if (commentIds.length > 0) {
    const { data: repliesCounts } = await supabase
      .from('comments')
      .select('parent_comment_id')
      .in('parent_comment_id', commentIds);

    if (repliesCounts) {
      repliesCounts.forEach((rc: any) => {
        const count = repliesCountMap.get(rc.parent_comment_id) || 0;
        repliesCountMap.set(rc.parent_comment_id, count + 1);
      });
    }
  }

  // Build a unique list of commenter user_ids
  const userIds = Array.from(
    new Set(
      comments
        .map((c: any) => c.user_id)
        .filter((id: string | null) => Boolean(id))
    )
  ) as string[];

  // Fetch safe public profiles via secure RPC (does not expose sensitive fields)
  let profilesMap = new Map<string, any>();
  if (userIds.length > 0) {
    const profiles = await getMultiplePublicProfiles(userIds);
    profilesMap = new Map(profiles.map((p: any) => [p.id, p]));
  }

  // Merge profiles into comments as "user" and add replies_count
  const safeComments = comments.map((comment: any) => ({
    ...comment,
    user: profilesMap.get(comment.user_id) || null,
    replies_count: repliesCountMap.get(comment.id) || 0,
  }));

  return safeComments;
}

export async function getCommentReplies(commentId: string) {
  // Fetch replies for specific comment
  const { data, error } = await supabase
    .from('comments')
    .select('*')
    .eq('parent_comment_id', commentId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(`Error fetching replies: ${error.message}`);

  const replies = data || [];

  // Get user profiles for reply authors
  const userIds = Array.from(
    new Set(
      replies
        .map((r: any) => r.user_id)
        .filter((id: string | null) => Boolean(id))
    )
  ) as string[];

  let profilesMap = new Map<string, any>();
  if (userIds.length > 0) {
    const profiles = await getMultiplePublicProfiles(userIds);
    profilesMap = new Map(profiles.map((p: any) => [p.id, p]));
  }

  const safeReplies = replies.map((reply: any) => ({
    ...reply,
    user: profilesMap.get(reply.user_id) || null,
  }));

  return safeReplies;
}

export async function deletePost(postId) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user || !user.id) {
    throw new Error("User not authenticated");
  }

  // RLS handles authorization: owners can delete their own posts,
  // admins/moderators can delete any post via RLS policy
  const { error } = await supabase
    .from('memory_posts')
    .delete()
    .eq('id', postId);

  if (error) {
    throw new Error(`Error deleting post: ${error.message}`);
  }

  return true;
}

// Delete a comment - RLS handles authorization (comment author, post owner, or admin/moderator)
export async function deleteComment(commentId: string) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user || !user.id) {
    throw new Error("User not authenticated");
  }

  const { error } = await supabase
    .from('comments')
    .delete()
    .eq('id', commentId);

  if (error) {
    throw new Error(`Error deleting comment: ${error.message}`);
  }

  return true;
}

export async function createPost(postData) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user || !user.id) {
    throw new Error("User not authenticated");
  }

  try {
    console.log("Creating post with data:", postData);
    // Create post in database
    const { data: postResult, error: postError } = await supabase
      .from('memory_posts')
      .insert([
        {
          user_id: user.id,
          pet_id: postData.pet_id || postData.petId,
          caption: postData.caption,
          privacy_setting: postData.privacy_setting || postData.privacySetting || 'public',
          post_type: postData.post_type || 'memory',
          contact_phone: postData.contact_phone ?? null,
          lost_lat: postData.lost_lat ?? null,
          lost_lng: postData.lost_lng ?? null,
          lost_address: postData.lost_address ?? null,
          lost_last_seen_at: postData.lost_last_seen_at ?? null,
        }
      ])
      .select('id')
      .single();

    if (postError) {
      throw new Error(`Error creating post: ${postError.message}`);
    }

    if (!postResult) {
      throw new Error('Failed to create post data');
    }

    console.log("Post created with ID:", postResult.id);

    // If there's media to upload
    if (postData.mediaFiles && postData.mediaFiles.length > 0) {
      console.log("Uploading media files:", postData.mediaFiles.length);
      
      for (const rawFile of postData.mediaFiles) {
        // Optimize images on the client before uploading. Videos and other
        // file types pass through unchanged.
        const file = rawFile.type.startsWith('image/')
          ? await optimizeImage(rawFile, { preset: 'feed' })
          : rawFile;
        const userId = user.id;
        const fileExt = file.name.split('.').pop();
        const filePath = `${userId}/${postData.pet_id || postData.petId}/${Date.now()}.${fileExt}`;

        console.log('Uploading file:', file.name, file.type, file.size, '(original:', rawFile.size, ')');

        // No need to check or create bucket - we'll assume the bucket already exists
        // and handle the error properly if it doesn't
        
        // Upload image to Supabase Storage
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from('memory-media')
          .upload(filePath, file);

        if (uploadError) {
          console.error("Upload error:", uploadError);
          
          if (uploadError.message.includes('new row violates row-level security policy')) {
            throw new Error(`Storage permission issue: Please contact admin to ensure the 'memory-media' bucket exists and you have proper permissions.`);
          }
          
          throw new Error(`Error uploading image: ${uploadError.message}`);
        }

        console.log("File uploaded successfully to path:", filePath);

        // Get public URL for the uploaded image
        const { data: { publicUrl } } = supabase.storage
          .from('memory-media')
          .getPublicUrl(filePath);

        console.log("Public URL generated:", publicUrl);

        // Determine if it's a video or image
        const isVideo = file.type.startsWith('video/');
        
        // Link the media file to the post
        const { error: mediaError } = await supabase
          .from('media_files')
          .insert([
            {
              user_id: userId,
              pet_id: postData.pet_id || postData.petId,
              post_id: postResult.id,
              file_path: filePath,
              mime_type: file.type,
              size_bytes: file.size,
              media_type: isVideo ? 'video_original' : 'image_original',
              ai_generated: false,
              url: publicUrl
            }
          ]);

        if (mediaError) {
          throw new Error(`Error linking media: ${mediaError.message}`);
        }
        
        console.log("Media file linked to post successfully");
      }
    }

    return postResult.id;
  } catch (error) {
    console.error("Error creating post:", error);
    throw error;
  }
}

// Update post caption and privacy setting
export async function updatePost(postId: string, updates: { caption?: string; privacy_setting?: 'public' | 'private' | 'followers_only' }) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user || !user.id) {
    throw new Error("User not authenticated");
  }

  // Verify the user owns the post
  const { data: post, error: postError } = await supabase
    .from('memory_posts')
    .select('user_id')
    .eq('id', postId)
    .single();

  if (postError) {
    throw new Error(`Error fetching post: ${postError.message}`);
  }

  if (post.user_id !== user.id) {
    throw new Error("You can only edit your own posts");
  }

  const { data, error } = await supabase
    .from('memory_posts')
    .update({
      caption: updates.caption,
      privacy_setting: updates.privacy_setting,
      updated_at: new Date().toISOString()
    })
    .eq('id', postId)
    .select()
    .single();

  if (error) {
    throw new Error(`Error updating post: ${error.message}`);
  }

  return data;
}

// Delete a single media file (DB row + storage object). Owner-only.
export async function deletePostMedia(mediaId: string) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) throw new Error("User not authenticated");

  // Fetch media (verify ownership via user_id)
  const { data: media, error: fetchError } = await supabase
    .from('media_files')
    .select('id, user_id, file_path, post_id')
    .eq('id', mediaId)
    .single();

  if (fetchError) throw new Error(`Error fetching media: ${fetchError.message}`);
  if (!media) throw new Error('Media not found');
  if (media.user_id !== user.id) throw new Error('You can only delete your own media');

  // Remove the storage object first (best-effort)
  if (media.file_path) {
    const { error: storageError } = await supabase.storage
      .from('memory-media')
      .remove([media.file_path]);
    if (storageError) {
      console.warn('Storage removal warning:', storageError.message);
    }
  }

  // Delete DB row
  const { error: delError } = await supabase
    .from('media_files')
    .delete()
    .eq('id', mediaId);

  if (delError) throw new Error(`Error deleting media: ${delError.message}`);

  return media.post_id;
}
